import {
  deriveDisplaySeverity,
  type PipelineBatchResult,
  type SeverityLevel,
} from '../../../../../../governance'

/**
 * GovernanceStats — top-level question-status summary.
 *
 * - pass / fail / review count QUESTIONS by verdict
 * - blockerCount / warningCount count ISSUES across all questions
 * - severityCounts is the 5-level display-only breakdown (Phase 1 Strak-Trek)
 *
 * Display severity is derived via `deriveDisplaySeverity(kind, severity)` to
 * keep the gate-verdict logic decoupled from how the UI presents intensity.
 */
export interface GovernanceStats {
  total: number
  pass: number
  fail: number
  review: number
  avgScore: number
  blockerCount: number
  warningCount: number
  severityCounts: Record<SeverityLevel, number>
}

const EMPTY_SEVERITY_COUNTS: Record<SeverityLevel, number> = {
  critical: 0,
  blocker: 0,
  review: 0,
  coach: 0,
  info: 0,
}

/**
 * Compute the aggregate GovernanceStats from a batch result.
 *
 * Empty-state contract: caller passes `totalQuestions` so the "total" tile
 * keeps a sensible value even before the first pipeline run completes.
 */
export function computeGovernanceStats(
  batchResult: PipelineBatchResult | null,
  totalQuestions: number,
): GovernanceStats {
  if (!batchResult) {
    return {
      total: totalQuestions,
      pass: 0,
      fail: 0,
      review: 0,
      avgScore: 0,
      blockerCount: 0,
      warningCount: 0,
      severityCounts: { ...EMPTY_SEVERITY_COUNTS },
    }
  }

  let blockerCount = 0
  let warningCount = 0
  const severityCounts: Record<SeverityLevel, number> = {
    critical: 0,
    blocker: 0,
    review: 0,
    coach: 0,
    info: 0,
  }

  for (const r of batchResult.results) {
    for (const i of r.gate.issues) {
      if (i.kind === 'blocker') blockerCount++
      else if (i.kind === 'coach') warningCount++
      const ds = deriveDisplaySeverity(i.kind, i.severity)
      severityCounts[ds]++
    }
  }

  return {
    total: totalQuestions,
    pass: batchResult.verdicts.PASS,
    fail: batchResult.verdicts.FAIL,
    review: batchResult.verdicts.REVIEW_NEEDED,
    avgScore: batchResult.avgScore,
    blockerCount,
    warningCount,
    severityCounts,
  }
}
